// service/crm/hooks/useSetContactImportCompanyFallbackMutation.ts

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  contactImportApi,
  contactImportQueryKeys,
} from "service/crm/api/contactImport";
import {
  ContactImportCompanyFallbackRequest,
  ContactImportCompanyMapping,
  ContactImportRecord,
} from "types/crm/contactImport";

interface CompanyFallbackVariables extends ContactImportCompanyFallbackRequest {
  importUuid: string;
}

/**
 * Settles the Company decision for every contact the import could not resolve.
 *
 * The saved import replaces what is cached, since the call resets the job and
 * the screens after this one read that state back.
 */
export const useSetContactImportCompanyFallbackMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<ContactImportRecord, unknown, CompanyFallbackVariables>({
    mutationFn: ({ importUuid, action, companyUuid }) =>
      contactImportApi.setCompanyFallback(importUuid, { action, companyUuid }),
    onSuccess: (record, variables) => {
      const targetUuid = record?.uuid || variables.importUuid;
      if (targetUuid) {
        // Prefix match: the page size in the key is not this call's business.
        queryClient.setQueriesData<ContactImportRecord>(
          { queryKey: contactImportQueryKeys.detail(targetUuid) },
          (old) => {
            if (!old) return record;
            return {
              ...old,
              ...record,
              uuid: old.uuid ?? record?.uuid ?? targetUuid,
              companyMapping: {
                ...old.companyMapping,
                ...record?.companyMapping,
                resolution: record?.companyMapping?.resolution ?? {
                  action: variables.action,
                  companyUuid: variables.companyUuid,
                },
              } as ContactImportCompanyMapping,
            };
          }
        );
      }
    },
  });
};
